import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CheckIcon from '@mui/icons-material/Check';
import LaunchIcon from '@mui/icons-material/Launch';
import { useAuth } from '../contexts/AuthContext';
import { companiesApi } from '../services/api';
import { ALL_SERVICES, SERVICE_DEFS, ServiceKey } from '../config/services';
import type { Company } from '../types';

const DRAWER_WIDTH = 268;

export type ShellView = 'home' | 'settings' | 'service';

interface Props {
  view: ShellView;
  activeServiceKey?: ServiceKey | null;
  onNavigate: (view: ShellView) => void;
  onOpenService: (key: ServiceKey) => void;
  onOpenSettings: () => void;
  children: React.ReactNode;
}

const AppShell: React.FC<Props> = ({
  view,
  activeServiceKey,
  onNavigate,
  onOpenService,
  onOpenSettings,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const authUser = user as { username?: string; role?: string } | null;
  const [companies, setCompanies] = useState<Company[]>([]);
  const [switching, setSwitching] = useState(false);
  const [orgAnchor, setOrgAnchor] = useState<null | HTMLElement>(null);

  const activeCompany = companies.find((c) => c.is_active);
  const enabledServices = useMemo(() => {
    const fromCo = activeCompany?.enabled_services;
    if (Array.isArray(fromCo) && fromCo.length > 0) {
      return fromCo.filter((k): k is ServiceKey => k in SERVICE_DEFS);
    }
    return [...ALL_SERVICES];
  }, [activeCompany]);

  const loadCompanies = useCallback(() => {
    companiesApi
      .list()
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleSwitchCompany = async (companyId: string) => {
    setOrgAnchor(null);
    if (companyId === activeCompany?.id) return;
    setSwitching(true);
    try {
      await companiesApi.activate(companyId);
      loadCompanies();
      window.location.reload();
    } finally {
      setSwitching(false);
    }
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2, minHeight: 64 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14, fontWeight: 700 }}>GP</Avatar>
        <Box sx={{ ml: 1.5, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap fontWeight={700}>
            VMCOM AIO
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            AI Content Platform
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List dense sx={{ px: 1, py: 1 }}>
        <ListItemButton
          selected={view === 'home'}
          onClick={() => {
            onNavigate('home');
            setMobileOpen(false);
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <HomeOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Дашборд" />
        </ListItemButton>
      </List>
      <Typography variant="overline" sx={{ px: 2, pt: 1, color: 'text.secondary' }}>
        Сервисы
      </Typography>
      <List dense sx={{ px: 1, flex: 1, overflow: 'auto' }}>
        {enabledServices.map((key) => {
          const s = SERVICE_DEFS[key];
          const Icon = s.icon;
          return (
            <ListItemButton
              key={key}
              selected={view === 'service' && activeServiceKey === key}
              onClick={() => {
                onOpenService(key);
                setMobileOpen(false);
              }}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Icon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary={s.title} secondary={s.subtitle} />
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <List dense sx={{ px: 1, pb: 1 }}>
        <ListItemButton
          selected={view === 'settings'}
          onClick={() => {
            onOpenSettings();
            setMobileOpen(false);
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Настройки" />
        </ListItemButton>
        <ListItemButton onClick={logout}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Выход" secondary={authUser?.username} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
            {view === 'settings'
              ? 'Настройки'
              : view === 'service' && activeServiceKey
                ? SERVICE_DEFS[activeServiceKey].title
                : 'Обзор системы'}
          </Typography>
          {view === 'service' && activeServiceKey && (
            <Tooltip title="Открыть в отдельной вкладке">
              <IconButton
                component="a"
                href={SERVICE_DEFS[activeServiceKey].href}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{ mr: 1 }}
              >
                <LaunchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Сменить организацию">
            <IconButton
              onClick={(e) => setOrgAnchor(e.currentTarget)}
              disabled={switching || companies.length === 0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                px: 1.5,
                gap: 1,
              }}
            >
              <BusinessOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 220 }} noWrap>
                {activeCompany?.name || 'Организация'}
              </Typography>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={orgAnchor} open={Boolean(orgAnchor)} onClose={() => setOrgAnchor(null)}>
            {companies.map((c) => (
              <MenuItem key={c.id} onClick={() => handleSwitchCompany(c.id)} selected={c.is_active}>
                {c.is_active ? <CheckIcon fontSize="small" sx={{ mr: 1 }} /> : <Box sx={{ width: 24, mr: 1 }} />}
                {c.name}
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, borderRight: 1, borderColor: 'divider' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          p: { xs: 2, sm: 3 },
          pt: { xs: 10, sm: 11 },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ flex: 1 }}>{children}</Box>
        <Typography
          component="footer"
          variant="caption"
          color="text.secondary"
          sx={{ mt: 4, py: 2, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}
        >
          © VMCOM 2019–{new Date().getFullYear()}. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default AppShell;
